/**
 * Photo capture — the one part of the survey that is bytes rather than JSON.
 *
 * `<input type="file" accept="image/*">` is the whole mechanism: no permissions
 * dance, no getUserMedia, no library, and it works with the radio off. There is
 * no upload here and no `fetch` — the Blob goes into IndexedDB through
 * `store.addPhoto`, which writes the metadata, the bytes and the outbox row in
 * ONE transaction (see db/store.ts). The sync engine carries it to R2 whenever
 * signal returns, last in the drain so a photo never delays a document.
 *
 * ── TWO SOURCES, TWO BUTTONS (D-015) ─────────────────────────────────────────
 * The design authority, 2026-08-06: *"You can take a picture and attach it to
 * the section (good), but you cannot upload photos from the camera roll, which
 * will be needed."*
 *
 * `capture="environment"` is why. It is not a hint — it FORCES the rear camera,
 * and the file picker (and with it the whole camera roll) becomes unreachable.
 * So there are now two inputs, differing in exactly that one attribute, behind
 * one full-size labelled button opening the OS's own picker sheet (D-030 R10).
 *
 * Two buttons rather than one control with an action sheet, because the audience
 * is a 60-year-old in a dim machine room who should not have to discover a
 * long-press to find half of the feature. A control that is not on the screen is
 * a control that does not exist.
 *
 * Both buttons run the SAME `capture()` handler and the same `store.addPhoto`
 * call, so a library photo is not a lesser photo: same PhotoRef shape, same R2
 * key, same `uploadState: 'pending'`, same outbox row, same offline behaviour.
 * The bytes are stored exactly as the file system handed them over — nothing
 * here re-encodes, rotates, or strips metadata — so a JPEG's EXIF orientation
 * arrives at R2 intact and is applied by the browser when it draws the
 * original, from either source; the thumbnail the phone makes beside it
 * (round 8.1, task 3) is decoded with the picture's own orientation for the
 * same reason. There is no orientation code here to keep in step.
 *
 * Photos attach at SECTION or QUESTION level (brief §5) and, when they satisfy
 * a recommended shot, carry that shot's id so `missingShots()` can tick it off.
 * Extras beyond the shot list are unlimited — spec §2.4 says so outright, and
 * nothing here counts or caps them.
 *
 * ── Deleting ─────────────────────────────────────────────────────────────────
 * Delete is a real labelled button under the thumbnail, not a 12px "×" in the
 * corner of it (brief §2). It calls `store.deletePhoto`, which removes the ref,
 * the bytes and the queued upload together — the UI never touches Dexie.
 *
 * ── ONE PHOTO THAT NEEDS YOU (the 2026-08-06 incident) ───────────────────────
 *
 * A photo the office refuses on its own merits — the wrong file type, too big,
 * a payload this build cannot make acceptable — is PARKED by the sync engine
 * rather than retried forever (see OutboxState 'blocked' in db/store.ts). The
 * tile it belongs to is where that fact belongs, because it is the one place on
 * the phone where the surveyor can see the actual picture and decide about it.
 * So a parked photo says so in plain words, right under its own thumbnail, and
 * offers the only two things a person can usefully do: send it once more, or
 * delete it and take it again. Both are full-size labelled buttons.
 *
 * The wording is deliberately calm and leads with the reassurance (D-015). The
 * survey is not in danger; one picture is waiting on a decision.
 *
 * ── THE LIVE CAMERA SHOT (the 2026-08-07 incident) ───────────────────────────
 *
 *   "When I take a picture, it says the photo is on my phone and the office
 *    cannot accept it… It accepts photos from the library without a problem,
 *    but a live shot won't be accepted."
 *
 * Both buttons ran the same handler, built the same meta, and produced the same
 * outbox row — so the difference was never here. It was in the FILE: a live iOS
 * capture can arrive with `file.type === ''`, and this handler used to write
 * `file.type || 'image/jpeg'` into the PhotoRef and then hand the UNTYPED file
 * straight to IndexedDB. Hours later the drain put that typeless Blob into a
 * multipart part, which the serializer is required to stamp
 * `application/octet-stream`, and the office refused it 415. Retaking produced
 * another typeless file, so the one action the message asked for could not work.
 *
 * Two things changed, and the order matters:
 *
 *   1. THE TYPE IS RESOLVED HERE, from the bytes if the phone will not say —
 *      `resolvePhotoType` (db/photo-file.ts). A JPEG announces itself in its
 *      first three bytes and cannot be talked out of it.
 *   2. THE BLOB IS STORED CARRYING THAT TYPE. `typedPhotoBlob` re-types it
 *      through `Blob.slice()`, which copies nothing, so the bytes in IndexedDB
 *      answer for themselves and no layer downstream has to guess.
 *
 * And a file the office genuinely cannot open is now refused AT CAPTURE, in one
 * plain sentence, while the surveyor is still standing in front of the thing
 * they photographed — not parked hours later in a basement.
 *
 * ── ONE TRUE ACTION, NOT TWO (the 2026-08-10 incident) ───────────────────────
 *
 * A parked tile used to offer both buttons unconditionally: send it again, or
 * delete it and take it again. That is right when the OFFICE refused the
 * picture — it is still on the phone and a second send is a reasonable thing to
 * want. It is wrong when the PHONE is what failed, and it is a lie when the
 * picture file itself is gone: iOS clears IndexedDB blobs under storage
 * pressure, and "Try sending it again" then offers to re-send bytes that no
 * longer exist.
 *
 * So the engine now says which side failed (`SyncAttention.side`) and whether a
 * second attempt could possibly help (`canRetry`), and the tile shows only the
 * action that can work. A control that cannot do anything is worse than no
 * control: it spends the one thing this audience has least of, which is
 * confidence that the app means what it says.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { PhotoRef } from '../../core/types';
import type { ApiClient } from '../../db/http-transport';
import type { SurveyStore } from '../../db/store';
import { makeThumbnail } from '../state/makeThumbnail';
import {
  planThumbnailSet,
  resolveOriginalBlob,
  resolveThumbsAsTheyLand,
  type PhotoByteSources,
} from '../state/photoBytes';
import { useServices } from '../state/services';
import { useSurvey } from '../state/SurveyProvider';
import { useSyncStatus } from '../state/useSyncStatus';
import { UPLOAD_LABEL, usePhotoCapture } from './usePhotoCapture';

export interface PhotoCaptureProps {
  sectionInstanceId: string;
  /** Absent = a section-level photo. */
  questionId?: string;
  /** Set when this capture satisfies a recommended shot from the shot list. */
  shotListId?: string;
  /**
   * What this photo is OF, used to name both buttons for a screen reader —
   * "Take photo — Machine room overview". The visible faces stay the same two
   * short verbs on every instance, so the pair is learned once.
   */
  label?: string;
  /** Icon-and-count only, for a shot-list row. */
  compact?: boolean;
  /**
   * False when this grid shows EXISTING photos with no door to a new one —
   * a question's own photos since D-033 R13 (the ✎ that opened the door is
   * gone and none was invented). The tiles keep their own actions; the add
   * button and the input it opens are not rendered, and a parked tile's
   * action reads "Delete photo" — there is nothing here to take again with.
   */
  canAdd?: boolean;
}

export function PhotoCapture({
  sectionInstanceId,
  questionId,
  shotListId,
  label = 'Add photo',
  compact = false,
  canAdd = true,
}: PhotoCaptureProps): ReactNode {
  const { sync } = useServices();
  const { doc, readOnly } = useSurvey();
  const status = useSyncStatus(sync);
  // THE capture path — one hook, shared with the photo shelf (D-028).
  const cam = usePhotoCapture({ sectionInstanceId, questionId, shotListId });
  const { busy, error, remove, retry } = cam;

  /**
   * photoId → the whole attention entry. Usually empty.
   *
   * The ENTRY, not just its sentence (2026-08-10): a photo whose picture file
   * the phone has cleared away cannot be re-sent by anybody, and offering "Try
   * sending it again" for it is offering a button that is guaranteed to do
   * nothing. `canRetry` is how the engine says which of those this is, so the
   * tile can show one true action instead of two and a coin toss.
   */
  const parked = new Map(
    status.attention
      .filter((entry) => entry.kind === 'photo' && entry.photoId !== undefined)
      .map((entry) => [entry.photoId as string, entry] as const),
  );

  const photos = doc.photos.filter(
    (photo) =>
      photo.sectionInstanceId === sectionInstanceId &&
      (questionId === undefined ? photo.questionId === undefined : photo.questionId === questionId) &&
      (shotListId === undefined || photo.shotListId === shotListId),
  );

  const thumbs = useThumbnails(photos);

  const needsAttention = photos.some((photo) => parked.has(photo.id));

  return (
    <div
      className={[
        'photos',
        compact ? 'photos--compact' : '',
        needsAttention ? 'photos--needs' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Two hidden inputs, two real buttons. The buttons are the accessible
       * controls; the inputs are only the pickers they open, and they differ in
       * exactly one attribute. `capture` FORCES the camera — which is the whole
       * reason the library needs an input of its own. Round 7 task 10, ruling
       * 12: none of this exists on a read-only survey. */}
      {canAdd && !readOnly ? <input {...cam.inputProps} /> : null}

      {photos.length > 0 ? (
        <ul className="photos__grid">
          {photos.map((photo) => {
            const src = thumbs[photo.id];
            const stuck = parked.get(photo.id);
            return (
              <li className={stuck ? 'photo photo--stuck' : 'photo'} key={photo.id}>
                {src ? (
                  <img className="photo__img" src={src} alt="" />
                ) : (
                  <span className="photo__img photo__img--none" aria-hidden="true">
                    ▣
                  </span>
                )}
                {/* `display`: it names where the bytes are, it never instructs.
                 * Still held to the 16px floor. "Will retry" would be a lie for
                 * a parked photo — it will not, until this surveyor says so. */}
                <span className="photo__state" data-legibility="display">
                  {stuck ? 'Needs you' : UPLOAD_LABEL[photo.uploadState]}
                </span>
                {stuck ? (
                  <p className="photo__stuck" data-legibility="helper">
                    {stuck.reason}
                  </p>
                ) : null}
                {stuck && stuck.canRetry !== false ? (
                  <button
                    type="button"
                    className="btn btn--ghost photo__retry"
                    onClick={() => void retry(photo.id)}
                  >
                    Try sending it again
                  </button>
                ) : null}
                {readOnly ? null : (
                  <button
                    type="button"
                    className="btn btn--ghost photo__remove"
                    onClick={() => void remove(photo.id)}
                  >
                    {stuck && canAdd ? 'Delete it and take it again' : 'Delete photo'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* ABOVE the buttons, not under them (D-011 item 6).
       *
       * This is what happened to the picture they just took, and the next thing
       * they will do about it is press one of the two controls immediately
       * below. An explanation printed underneath the control it is about is an
       * explanation read second, or scrolled past. */}
      {error ? (
        <p className="photos__error" role="alert">
          {error}
        </p>
      ) : null}

      {/* ONE control (D-030 R10): the OS sheet offers Take Photo / Photo * Library / Choose Files itself the moment the input opens without a * `capture` attribute — the app's own two-option menu restated it. */}
      {canAdd && !readOnly ? (
        <div className="photos__actions">
                <button
                  type="button"
                  className="btn btn--ghost photos__add"
                  disabled={busy}
                  aria-label={`Add photo — ${label}`}
                  onClick={() => cam.open()}
                >
                  <span aria-hidden="true">📷</span>{' '}
                  {busy ? 'Saving…' : photos.length > 0 ? `Add photo (${photos.length})` : 'Add photo'}
                </button>
              </div>
      ) : null}
    </div>
  );
}

/**
 * How many photos resolve at once. A survey of "Mema's House"'s size carries
 * 120; on a phone that has lost its bytes every one of them is a `GET`, and
 * unbounded that was 120 concurrent requests in one render (final review of
 * round 8). Four keeps the office busy without swamping a phone's radio, and
 * a local hit still returns in the time it takes to read one row.
 */
const THUMBNAIL_CONCURRENCY = 4;

/**
 * The real sources behind src/ui/state/photoBytes.ts: the store's two byte
 * tables, the office's two sizes of one photo, and the phone's own canvas.
 */
function byteSources(store: SurveyStore, api: ApiClient): PhotoByteSources {
  return {
    getLocal: async (photoId) => (await store.getPhotoBlob(photoId))?.blob,
    fetchRemote: (surveyId, photoId) => api.fetchPhoto(surveyId, photoId),
    putLocal: (photoId, surveyId, blob) => store.putPhotoBlob(photoId, surveyId, blob),
    getThumb: async (photoId) => (await store.getPhotoThumb(photoId))?.data,
    putThumb: (photoId, surveyId, bytes) => store.putPhotoThumb(photoId, surveyId, bytes),
    fetchRemoteThumb: (surveyId, photoId) => api.fetchPhoto(surveyId, photoId, 'thumb'),
    makeThumb: (original) => makeThumbnail(original),
  };
}

/**
 * Object URLs of the THUMBNAILS for a set of photos. The bytes come through
 * `resolveThumbBlob` (src/ui/state/photoBytes.ts): the phone's own thumb row,
 * the office's thumb, a thumb made from the original on the phone, and last
 * the office's original — each kept so the next render is local again —
 * `THUMBNAIL_CONCURRENCY` at a time. Every URL is published the moment its
 * thumb resolves (round 8.1, task 4), so a hand whose bytes were all lost
 * fills card by card rather than all at once after the slowest fetch. A
 * picture neither side has simply has no URL and the tile falls back to a
 * placeholder — the photo is still on the record, it just has no bytes
 * anywhere this device can reach.
 *
 * When the SET changes (a capture, a delete), only the ids that left are
 * revoked and only the ids that are new are resolved — `planThumbnailSet`,
 * proved in the node suite. The first cut (final review's fix wave, D-040
 * postscript) revoked every URL it had minted in the effect's cleanup and
 * then kept those revoked strings in state for the surviving ids, so each
 * capture drew the neighbours from URLs that pointed at nothing for a beat
 * and re-resolved every card. A URL now lives exactly as long as its id is
 * in the set, or until unmount, and a revoked URL is never left in state.
 */
export function useThumbnails(refs: readonly PhotoRef[]): Record<string, string> {
  const { store, api } = useServices();
  const [urls, setUrls] = useState<Record<string, string>>({});
  // The live map, read by the effect without going through a stale closure:
  // `urls` at effect time is whatever render the effect belongs to, and a
  // thumb can land between renders.
  const held = useRef<Record<string, string>>({});
  // Ids whose resolution has started and not yet landed — so a set change
  // mid-flight (a second capture before the first's thumb resolved) does not
  // ask for the same picture twice.
  const inFlight = useRef(new Set<string>());
  const mounted = useRef(true);
  // The ids are the identity that matters: callers build `refs` inline on
  // every render, so the array itself is never stable, and a label edit or an
  // upload-state tick must not tear down and remint every object URL.
  const key = refs.map((ref) => ref.id).join(',');
  const latest = useRef(refs);
  latest.current = refs;
  const wantedNow = useRef(new Set<string>());

  useEffect(() => {
    const wanted = key === '' ? [] : key.split(',');
    wantedNow.current = new Set(wanted);
    const plan = planThumbnailSet(held.current, wanted, inFlight.current);

    for (const url of plan.revoke) URL.revokeObjectURL(url);
    if (plan.keep !== held.current) {
      held.current = plan.keep;
      setUrls(plan.keep);
    }

    const byId = new Map(latest.current.map((ref) => [ref.id, ref]));
    const targets = plan.resolve.flatMap((id) => {
      const ref = byId.get(id);
      return ref ? [ref] : [];
    });
    if (targets.length === 0) return;
    for (const ref of targets) inFlight.current.add(ref.id);

    void resolveThumbsAsTheyLand(targets, byteSources(store, api), THUMBNAIL_CONCURRENCY, (ref, blob) => {
      inFlight.current.delete(ref.id);
      // Landed for a photo that has since left the set, or after unmount:
      // nothing will draw it, so it is not minted at all.
      if (!mounted.current || !wantedNow.current.has(ref.id)) return;
      const url = URL.createObjectURL(blob);
      held.current = { ...held.current, [ref.id]: url };
      setUrls(held.current);
    })
      .catch(() => undefined)
      .finally(() => {
        for (const ref of targets) inFlight.current.delete(ref.id);
      });
  }, [store, api, key]);

  // Unmount alone: every URL still held goes, and nothing that lands after
  // is minted. Kept apart from the effect above, whose cleanup runs on every
  // set change — where revoking would be the first cut's bug again.
  useEffect(() => {
    // Set on mount as well as read at unmount, so StrictMode's rehearsal
    // unmount in development does not leave the hook believing it is gone.
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const url of Object.values(held.current)) URL.revokeObjectURL(url);
      held.current = {};
    };
  }, []);

  return urls;
}

/**
 * The object URL of ONE photo's original, for the viewer (round 8.1, task
 * 4): resolved when the viewer opens on it, through `resolveOriginalBlob` —
 * the phone's own row, else the office's copy, kept — and revoked when the
 * viewer closes or moves to another photo. Undefined until it has arrived;
 * the viewer shows the thumb in the meantime. Null means nothing is open.
 */
export function useOriginal(ref: PhotoRef | null): string | undefined {
  const { store, api } = useServices();
  const [held, setHeld] = useState<{ id: string; url: string } | null>(null);
  const id = ref?.id ?? null;
  const latest = useRef(ref);
  latest.current = ref;

  useEffect(() => {
    const target = latest.current;
    if (!target) return;
    let cancelled = false;
    let created: string | undefined;

    // `resolveOriginalBlob` guards its own sources, so this `.catch` is the
    // second line (final review's fix wave): a rejection out of a render's
    // effect must never become an unhandled one, and a picture that cannot
    // be resolved is a viewer that keeps showing the thumb.
    void resolveOriginalBlob(byteSources(store, api), target)
      .then((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setHeld({ id: target.id, url });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (created) {
        URL.revokeObjectURL(created);
        // Forgotten as well as revoked: a revoked URL handed to the next open
        // of the same photo would draw nothing.
        setHeld((current) => (current?.url === created ? null : current));
      }
    };
  }, [store, api, id]);

  return held && held.id === id ? held.url : undefined;
}
